import { DEFAULT_LOCALE } from "@/i18n/routing";
import { TRIPLA_API_URL, TRIPLA_VERSION } from "@/constants/enviroment";
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { EHttpStatusCode } from "./api";
import { clientSessionManager } from "./clientSessionManager";

interface IApiConfig {
  baseURL: string;
  headers: Record<string, string>;
}

// API Base URL
const apiConfig: IApiConfig = {
  baseURL: TRIPLA_API_URL || "",
  headers: {
    "App-Version": TRIPLA_VERSION || "",
  },
};

const axiosInstance: AxiosInstance = axios.create(apiConfig);

// Request Interceptor: Attach `client_session` to headers
axiosInstance.interceptors.request.use(
  async (config) => {
    // Dynamically update the `Tripla-Locale` header
    config.headers["Tripla-Locale"] = DEFAULT_LOCALE;

    try {
      const session = await clientSessionManager.ensureValidSession();
      if (session) {
        config.headers["Client-Session"] = session;
      } else {
        console.warn("No valid client session available for request");
      }
    } catch (error) {
      console.error("Failed to ensure client session:", error);
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// Response Interceptor: Handle session expiry and retry logic
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      [EHttpStatusCode.Forbidden, EHttpStatusCode.Unauthorized].includes(
        error.response?.status,
      ) &&
      !originalRequest._retry // Prevent infinite retry loop
    ) {
      originalRequest._retry = true;

      try {
        console.log("Session expired, refreshing...");
        const newSession = await clientSessionManager.refreshSession();

        if (newSession) {
          // Update the failed request with new session
          originalRequest.headers["Client-Session"] = newSession;
          return axiosInstance.request(originalRequest);
        } else {
          console.error("Failed to refresh session, request cannot be retried");
          throw new Error("Session refresh failed");
        }
      } catch (refreshError) {
        console.error("Session refresh failed:", refreshError);
        throw refreshError;
      }
    }

    throw error;
  },
);

// API methods
const triplaApi = {
  get: <T>(
    url: string,
    config: AxiosRequestConfig = {},
  ): Promise<AxiosResponse<T>> => axiosInstance.get<T>(url, config),

  post: <T, D>(
    url: string,
    data: D,
    config?: AxiosRequestConfig<FormData> | undefined,
  ): Promise<AxiosResponse<T>> => axiosInstance.post<T>(url, data, config),

  put: <T, D>(
    url: string,
    data: D,
    config?: AxiosRequestConfig<FormData> | undefined,
  ): Promise<AxiosResponse<T>> => axiosInstance.put<T>(url, data, config),

  delete: <T>(
    url: string,
    config?: AxiosRequestConfig<FormData> | undefined,
  ): Promise<AxiosResponse<T>> => axiosInstance.delete<T>(url, config),
};

export default triplaApi;
